import { AlertCircle, Check } from 'lucide-react';

import { ageDays, ageLabel, formatAbsoluteDate } from '../../../utils/relative-age';

export interface VerifiedAgeProps {
  /** When a person last checked the record, or nothing if nobody has. */
  at: string | null | undefined;
  /** Drops the word "verified" for tight rows. */
  compact?: boolean;
  now?: Date;
}

/** Older than this and the check is likely out of date. */
export const STALE_AFTER_DAYS = 90;

const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** How long ago a record was verified. Turns amber past 90 days; the exact date shows on hover. */
export function VerifiedAge({ at, compact, now }: VerifiedAgeProps) {
  if (!at) {
    return (
      <span style={{ fontSize: 10.5, color: 'var(--nb-muted)', fontStyle: 'italic' }}>
        Never verified
      </span>
    );
  }
  const stale = ageDays(at, now) > STALE_AFTER_DAYS;
  const Icon = stale ? AlertCircle : Check;
  const age = ageLabel(at, now);
  return (
    <span
      title={`Verified ${formatAbsoluteDate(at)}`}
      data-stale={stale ? 'true' : 'false'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10.5,
        fontWeight: stale ? 700 : 500,
        color: stale ? 'var(--nb-warn)' : 'var(--nb-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={11} aria-hidden="true" />
      {compact ? age : `verified ${age}`}
      {stale && <span style={srOnly}> (may be out of date)</span>}
    </span>
  );
}
